import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import UserIcon from '@BUP/assets/Icons/user_icon.svg'
import PageLayout from '@BUP/components/Layout/Page'
import TitleDashboard from '@BUP/components/Generics/TitleDashboard'
import BupSpinner from '@BUP/components/Loading/Spinner'
import AlertComponent from '@BUP/components/Generics/Alert'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import PreferencesTable from '@BUP/components/CourseAssignment/PreferencesTable'
import { useGetCurrentPeriodStatus } from '@BUP/hooks/CourseAssignment/useGetCurrentPeriodStatus'
import { useGetPeriodPreferences } from '@BUP/hooks/CourseAssignment/useGetPeriodPreferences'

/**
 * Own screen for a period's student responses, split out from the period dashboard - a full
 * grade can mean hundreds of rows, too many to embed inline on the same page as the rest of the
 * period's controls.
 */
const CourseAssignmentResponsesPage = () => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  const navigate = useNavigate()
  // Different grades can each have their own period open at once (see the backend's openPeriod
  // doc comment), so "the current period" needs to know which grade the counselor came from -
  // carried here as a query param from the period dashboard's grade selector.
  // The period history links straight to one period by id instead (it may be a closed, older
  // one that is no longer any grade's "current" period) - when present, it wins over `grade`.
  const [searchParams] = useSearchParams()
  const periodIdParam = searchParams.get('periodId')
  const grade = periodIdParam ? null : searchParams.get('grade')

  const { isLoading: isLoadingCurrent, error: errorPeriod, period: currentPeriod } = useGetCurrentPeriodStatus(grade)
  const periodId = periodIdParam || currentPeriod?.id
  const isLoadingPeriod = !periodIdParam && isLoadingCurrent
  const {
    isLoading: isLoadingPreferences,
    error: errorPreferences,
    preferences
  } = useGetPeriodPreferences(periodId)

  const renderContent = () => {
    if (isLoadingPeriod) return <BupSpinner size='large' />

    if (errorPeriod) {
      return (
        <AlertComponent
          type='error'
          messageKey='common.resources.course-assignment.actions.read.failure'
          error={errorPeriod}
        />
      )
    }

    if (!periodId) {
      return <AlertComponent type='info' message={t('noPeriod')} />
    }

    return (
      <PreferencesTable
        isLoading={isLoadingPreferences}
        error={errorPreferences}
        preferences={preferences}
      />
    )
  }

  return (
    <PageLayout>
      <>
        <div style={{ marginBottom: '1rem' }}>
          <ButtonComponent
            buttonText={t('backToPeriodButton')}
            colorType='tertiary'
            size='small'
            onClick={() => navigate(periodIdParam ? '/course-assignment/periods' : '/course-assignment/period')}
          />
        </div>
        <TitleDashboard icon={UserIcon} text={t('preferencesSectionTitle')} />
        {renderContent()}
      </>
    </PageLayout>
  )
}

export default CourseAssignmentResponsesPage
